// src/App.js - Complete WITH built-in  analytics tracker
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import React, { useEffect } from 'react';
import BlogDetail from './pages/BlogDetail';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useTheme } from './context/ThemeContext';
import { useAuth } from './context/AuthContext';
import Breadcrumb from './components/Breadcrumb';
import useSEO from './hooks/useSEO';


// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import ThemeToggle from './components/ThemeToggle';
import LoadingSpinner from './components/LoadingSpinner';
import LiveChat from './components/LiveChat';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Portfolio from './pages/Portfolio';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SingleFeedback from './pages/SingleFeedback';
import Feedback from './pages/Feedback';
import Sitemap from './pages/Sitemap';
import Careers from './pages/Careers';
import Forum from './pages/Forum';
import Blog from './pages/Blog';
import FAQ from './pages/FAQ';

// Services
import ServicesOverview from './pages/Services/ServicesOverview';
import SoftwareDevelopment from './pages/Services/SoftwareDevelopment';
import CloudComputing from './pages/Services/CloudComputing';
import CyberSecurity from './pages/Services/CyberSecurity';
import VLSIDesign from './pages/Services/VLSIDesign';
import AIMLSolutions from './pages/Services/AIMLSolutions';
import DataAnalytics from './pages/Services/DataAnalytics';
import VLSIServices from './pages/Services/VLSIServices';
import BlockchainWeb3 from './pages/Services/BlockchainWeb3';
import GameDevelopment from './pages/Services/GameDevelopment';
import AIAutomation from './pages/Services/AIAutomation';
import StartupMVP from './pages/Services/StartupMVP';

// Auth
import ClientLogin from './auth/ClientLogin';
import ClientSignup from './auth/ClientSignup';
import ClientDashboard from './auth/ClientDashboard';

// Admin
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import ManageNews from './admin/ManageNews';
import AdminChat from './admin/AdminChat';
import ManageFeedback from './admin/ManageFeedback';
import AnalyticsDashboard from './admin/AnalyticsDashboard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function ScrollRestoration() {
  const location = useLocation();

  useEffect(() => {
    // Check if we're navigating back/forward (has a location.key)
    if (location.key) {
      const savedPosition = sessionStorage.getItem(`scrollPos_${location.key}`);
      
      if (savedPosition) {
        // If coming back via browser back/forward, restore exact position
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
        }, 0);
      }
    }

    // Function to save current scroll position
    const saveScrollPosition = () => {
      sessionStorage.setItem(`scrollPos_${location.key}`, window.scrollY.toString());
    };

    // Save position when user navigates away
    window.addEventListener('beforeunload', saveScrollPosition);
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
      saveScrollPosition();
    };
  }, [location.key]);

  return null;
}

// Inline analytics tracker (replaces utils/analyticsTracker)
function AnalyticsWrapper({ children }) {
  const location = useLocation();
  const { user } = useAuth() || {};

  useEffect(() => {
    // ensure we have a session id
    let sessionId = localStorage.getItem('analytics_sid');
    if (!sessionId) {
      const random =
        (window.crypto &&
          window.crypto.randomUUID &&
          window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionId = random;
      localStorage.setItem('analytics_sid', sessionId);
    }

    // send page view
    fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        currentPage: window.location.pathname,
        userId: user?.id || user?.email || null,
      }),
    }).catch(() => {
      // ignore analytics errors
    });
  }, [location.pathname, user?.id, user?.email]);

  return children;
}

function AppRoutes() {
  const { user } = useAuth() || {};
  useSEO();
  const ProtectedRoute = ({ children, adminOnly = false, allowedRoles = null }) => {
    if (!user) {
      return <Navigate to={adminOnly ? '/admin/login' : '/login'} replace />;
    }

    // If specific roles are allowed, check against that list
    if (allowedRoles) {
      if (!allowedRoles.includes(user.role)) {
        // Redirect based on user's role
        if (user.role === 'admin') {
          return <Navigate to="/admin/dashboard" replace />;
        }
        return <Navigate to="/client/dashboard" replace />;
      }
      return children;
    }

    // For admin-only routes (adminOnly = true)
    if (adminOnly && user.role !== 'admin') {
      // Only admins can access admin-only routes
      return <Navigate to="/client/dashboard" replace />;
    }

    // For client routes (adminOnly = false)
    if (!adminOnly && user.role === 'admin') {
      // Admins redirect to admin dashboard
      return <Navigate to="/admin/dashboard" replace />;
    }

    return children;
  };



  return (
    <>
      <ScrollToTop />
      <ScrollRestoration />
      <AnalyticsWrapper>
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
          <Navbar />
          <Breadcrumb />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/feedback/:id" element={<Feedback />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/faq" element={<FAQ />} />
            

            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/sitemap" element={<Sitemap />} />

            {/* Services Routes */}
            <Route path="/services" element={<ServicesOverview />} />
            <Route
              path="/services/software-development"
              element={<SoftwareDevelopment />}
            />
            <Route
              path="/services/cloud-computing"
              element={<CloudComputing />}
            />
            <Route
              path="/services/cyber-security"
              element={<CyberSecurity />}
            />
            <Route path="/services/vlsi-design" element={<VLSIDesign />} />
            <Route
              path="/services/ai-ml-solutions"
              element={<AIMLSolutions />}
            />
            <Route path="/services/data-analytics" element={<DataAnalytics />} />
            <Route path="/services/vlsi" element={<VLSIServices />} />
            <Route path="/services/blockchain" element={<BlockchainWeb3 />} />
            <Route
              path="/services/game-development"
              element={<GameDevelopment />}
            />
            <Route path="/services/ai-automation" element={<AIAutomation />} />
            <Route path="/services/startup-mvp" element={<StartupMVP />} />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                user?.role === 'admin' ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <ClientLogin />
                )
              }
            />
            <Route
              path="/signup"
              element={
                user ? <Navigate to="/client/dashboard" /> : <ClientSignup />
              }
            />
            <Route
              path="/client/dashboard"
              element={
                <ProtectedRoute>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/login"
              element={
                user?.role === 'admin' ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <AdminLogin />
                )
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute adminOnly>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/manage-news"
              element={
                <ProtectedRoute adminOnly>
                  <ManageNews />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/manage-feedback"
              element={
                <ProtectedRoute adminOnly>
                  <ManageFeedback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/chat"
              element={
                <ProtectedRoute allowedRoles={['admin', 'support_agent']}>
                  <AdminChat />
                </ProtectedRoute>
              }
            />

            {/* 404 Page */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <div className="text-center p-8">
                    <h1 className="text-6xl font-bold text-blue-600 dark:text-blue-400 mb-4">
                      404
                    </h1>
                    <p className="text-2xl mb-4 text-gray-900 dark:text-white">
                      Page Not Found
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                      The page you're looking for doesn't exist.
                    </p>
                    <a
                      href="/"
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition inline-block"
                    >
                      Go Back Home
                    </a>
                  </div>
                </div>
              }
            />
          </Routes>

          <Footer />
          <LiveChat /> 
          <BackToTop />
          <ThemeToggle />
        </div>
      </AnalyticsWrapper>
    </>
  );
}

function App() {
  const { theme } = useTheme() || {};
  const { loading } = useAuth() || {};

  if (loading) {
    return (
      <LoadingSpinner fullScreen text="Loading NEVXOAULIO VENTURES..." />
    );
  }

  return (
    <div className={theme}>
      <Router>
        <AppRoutes />
      </Router>
    </div>
  );
}

export default App;
